<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="ns-month" :class="{'ns-month--open': isOpen}">
    <header class="ns-month__header">
      <button
        type="button"
        class="ns-month__nav"
        aria-label="Mese precedente"
        @click="shift(-1)"
      >
        ‹
      </button>
      <div class="ns-month__title">
        <span class="ns-month__label">{{ periodLabel }}</span>
        <span v-if="ownerName" class="ns-month__owner">{{ ownerName }}</span>
      </div>
      <button
        type="button"
        class="ns-month__nav"
        aria-label="Mese successivo"
        :disabled="!canGoNext"
        @click="shift(1)"
      >
        ›
      </button>
    </header>

    <main class="ns-month__body">
      <p v-if="report && report.locked" class="ns-month__locked">
        Mese chiuso il {{ lockedDate }}: non modificabile.
      </p>

      <div v-if="error" class="ns-month__error" role="alert">
        <p>{{ error }}</p>
        <button type="button" @click="$emit('retry')">Riprova</button>
      </div>

      <div v-else-if="!report" class="ns-month__loading">
        <span class="ns-month__spinner" aria-hidden="true"></span>
        Caricamento del mese…
      </div>

      <template v-else>
        <section
          class="ns-totals"
          :class="{'ns-totals--stale': loading}"
          aria-label="Totali del mese"
        >
          <div class="ns-totals__grand">
            <span>Totale del mese</span>
            <strong>{{ euro(report.totals.grand) }}</strong>
          </div>
          <dl class="ns-totals__parts">
            <div>
              <dt>Km</dt>
              <dd>{{ km(report.totals.km) }}</dd>
            </div>
            <div>
              <dt>Rimborso km</dt>
              <dd>{{ euro(report.totals.trips) }}</dd>
            </div>
            <div>
              <dt>Spese</dt>
              <dd>{{ euro(report.totals.expenses) }}</dd>
            </div>
          </dl>
        </section>

        <p v-if="report.totals.missingRateCount > 0" class="ns-month__warning">
          {{ missingRateText }}
        </p>

        <p v-if="entries.length === 0" class="ns-month__empty">
          Nessuna trasferta o spesa in {{ periodLabel.toLowerCase() }}.
          <template v-if="isOpen">
            <br />Usa i pulsanti in basso per aggiungerne una.
          </template>
        </p>

        <ul v-else class="ns-list">
          <li v-for="entry in entries" :key="entry.key">
            <component
              :is="isOpen ? 'button' : 'div'"
              :type="isOpen ? 'button' : undefined"
              class="ns-row"
              :class="`ns-row--${entry.kind}`"
              @click="isOpen && onEdit(entry)"
            >
              <span class="ns-row__date">
                <strong>{{ entry.day }}</strong>
                <small>{{ entry.monthShort }}</small>
              </span>
              <span class="ns-row__head">
                {{ entry.kind === 'trip' ? entry.from : entry.title }}
                <svg
                  v-if="entry.hasReceipt"
                  class="ns-row__clip"
                  viewBox="0 0 24 24"
                  role="img"
                  aria-label="con ricevuta"
                >
                  <path
                    d="M16.5 6.5v10a4.5 4.5 0 0 1-9 0V5a3 3 0 0 1 6 0v10.5a1.5 1.5 0 0 1-3 0V6.5"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round"
                  />
                </svg>
              </span>
              <span class="ns-row__amount">{{ entry.amount }}</span>
              <span v-if="entry.to" class="ns-row__line ns-row__head">
                → {{ entry.to }}
              </span>
              <span
                v-if="entry.meta || entry.missingRate"
                class="ns-row__line ns-row__meta"
              >
                {{ entry.meta }}
                <small v-if="entry.missingRate" class="ns-row__tag">
                  senza tariffa
                </small>
              </span>
              <span v-if="entry.note" class="ns-row__line ns-row__note">
                {{ entry.note }}
              </span>
            </component>
          </li>
        </ul>
      </template>
    </main>

    <footer v-if="isOpen" class="ns-month__actions">
      <button
        type="button"
        class="ns-month__add ns-month__add--trip"
        @click="$emit('new-trip')"
      >
        + Trasferta
      </button>
      <button
        type="button"
        class="ns-month__add ns-month__add--expense"
        @click="$emit('new-expense')"
      >
        + Spesa
      </button>
    </footer>
  </div>
</template>

<script>
import {formatEuro, formatKm} from '@/orangehrmNoteSpesePlugin/util/money';

const MONTHS = [
  'Gennaio',
  'Febbraio',
  'Marzo',
  'Aprile',
  'Maggio',
  'Giugno',
  'Luglio',
  'Agosto',
  'Settembre',
  'Ottobre',
  'Novembre',
  'Dicembre',
];

// Le etichette di OpenRouteService sono indirizzi completi ("Via Roma 1,
// Milano, MI, Italia"): nell'elenco bastano le prime due parti.
function shortPlace(label) {
  return label
    .split(',')
    .slice(0, 2)
    .map((part) => part.trim())
    .join(', ');
}

export default {
  name: 'MonthView',

  props: {
    period: {
      type: Object,
      required: true,
    },
    report: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: '',
    },
    canGoNext: {
      type: Boolean,
      default: false,
    },
    ownerName: {
      type: String,
      default: '',
    },
  },

  emits: [
    'change-period',
    'retry',
    'new-trip',
    'new-expense',
    'edit-trip',
    'edit-expense',
  ],

  computed: {
    periodLabel() {
      return `${MONTHS[this.period.month - 1]} ${this.period.year}`;
    },

    // Aperto solo quando il riepilogo del mese mostrato è arrivato e non è
    // chiuso: finché carica non si offrono pulsanti che poi sparirebbero.
    isOpen() {
      return !!this.report && !this.report.locked && !this.error;
    },

    lockedDate() {
      const lockedAt = this.report.lockedAt;
      if (!lockedAt) return '';
      const [year, month, day] = lockedAt.substring(0, 10).split('-');
      return `${day}/${month}/${year}`;
    },

    missingRateText() {
      const n = this.report.totals.missingRateCount;
      return n === 1
        ? "1 trasferta senza tariffa: avvisa l'ufficio del personale."
        : `${n} trasferte senza tariffa: avvisa l'ufficio del personale.`;
    },

    // Trasferte e spese in un solo elenco, le più recenti in alto: è lì che
    // si guarda subito dopo aver aggiunto una voce.
    entries() {
      const trips = this.report.trips.map((trip) => ({
        key: `t${trip.id}`,
        kind: 'trip',
        id: trip.id,
        date: trip.date,
        from: shortPlace(trip.origin),
        to: shortPlace(trip.destination),
        meta: `${formatKm(trip.km)}${
          trip.roundTrip ? ' · andata e ritorno' : ''
        }`,
        note: trip.purpose,
        amount: formatEuro(trip.amount),
        missingRate: trip.missingRate,
        hasReceipt: false,
      }));
      const expenses = this.report.expenses.map((expense) => ({
        key: `e${expense.id}`,
        kind: 'expense',
        id: expense.id,
        date: expense.date,
        title: expense.categoryLabel,
        meta: expense.description || '',
        note: '',
        amount: formatEuro(expense.amount),
        missingRate: false,
        hasReceipt: expense.hasReceipt,
      }));

      return trips
        .concat(expenses)
        .sort((a, b) =>
          a.date === b.date ? b.id - a.id : a.date < b.date ? 1 : -1,
        )
        .map((entry) => {
          const [, month, day] = entry.date.split('-');
          return {
            ...entry,
            day,
            monthShort: MONTHS[parseInt(month, 10) - 1]
              .substring(0, 3)
              .toLowerCase(),
          };
        });
    },
  },

  methods: {
    euro: formatEuro,
    km: formatKm,

    shift(delta) {
      const index = this.period.year * 12 + (this.period.month - 1) + delta;
      this.$emit('change-period', {
        year: Math.floor(index / 12),
        month: (index % 12) + 1,
      });
    },

    onEdit(entry) {
      this.$emit(
        entry.kind === 'trip' ? 'edit-trip' : 'edit-expense',
        entry.id,
      );
    },
  },
};
</script>

<style scoped>
.ns-month {
  max-width: 560px;
  margin: 0 auto;
}

.ns-month--open {
  padding-bottom: calc(92px + env(safe-area-inset-bottom, 0px));
}

.ns-month__header {
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 4px;
  padding-top: calc(6px + env(safe-area-inset-top, 0px));
  background: #ffffff;
  border-bottom: 3px solid var(--ns-primary);
}

.ns-month__nav {
  flex: 0 0 56px;
  height: 56px;
  border: 0;
  border-radius: 12px;
  background: transparent;
  color: var(--ns-primary);
  font-size: 40px;
  line-height: 1;
  font-weight: 700;
  -webkit-tap-highlight-color: transparent;
}

.ns-month__nav:active {
  background: #f2f3f5;
}

.ns-month__nav:disabled {
  color: #c9ccd2;
}

.ns-month__title {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 0;
}

.ns-month__label {
  font-size: 21px;
  font-weight: 800;
}

.ns-month__owner {
  font-size: 14px;
  color: var(--ns-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.ns-month__body {
  padding: 12px;
}

.ns-month__locked {
  margin: 0 0 12px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #e3e5e9;
  color: #2f343c;
  font-weight: 600;
}

.ns-month__warning {
  margin: 0 0 12px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #fff3cd;
  border: 1px solid #e8c252;
  color: #5c4400;
  font-weight: 600;
}

.ns-month__error {
  padding: 16px;
  border-radius: 12px;
  background: #fdecea;
  color: #8c1d18;
}

.ns-month__error p {
  margin: 0 0 12px;
}

.ns-month__error button {
  min-height: 44px;
  padding: 0 20px;
  border: 1px solid #8c1d18;
  border-radius: 10px;
  background: #ffffff;
  color: #8c1d18;
  font: inherit;
  font-weight: 700;
}

.ns-month__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 48px 0;
  color: var(--ns-muted);
}

.ns-month__spinner {
  width: 22px;
  height: 22px;
  border: 3px solid #d7dae0;
  border-top-color: var(--ns-primary);
  border-radius: 50%;
  animation: ns-month-spin 0.8s linear infinite;
}

@keyframes ns-month-spin {
  to {
    transform: rotate(360deg);
  }
}

.ns-totals {
  margin-bottom: 12px;
  padding: 16px;
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  transition: opacity 0.2s;
}

.ns-totals--stale {
  opacity: 0.55;
}

.ns-totals__grand {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e6e8ec;
}

.ns-totals__grand span {
  font-weight: 700;
}

.ns-totals__grand strong {
  font-size: 30px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.ns-totals__parts {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin: 12px 0 0;
}

.ns-totals__parts dt {
  font-size: 13px;
  color: var(--ns-muted);
}

.ns-totals__parts dd {
  margin: 2px 0 0;
  font-size: 17px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.ns-month__empty {
  margin: 32px 12px;
  text-align: center;
  color: var(--ns-muted);
}

.ns-list {
  list-style: none;
  margin: 0;
  padding: 0;
  border-radius: 14px;
  overflow: hidden;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
}

.ns-list li + li {
  border-top: 1px solid #eceef1;
}

/*
 * Griglia: data a sinistra, importo in alto a destra, e le righe dopo la
 * prima prendono anche lo spazio sotto l'importo. Partenza e arrivo stanno
 * così su due righe larghe: su una sola l'arrivo, che è ciò che si cerca
 * con lo sguardo, finirebbe tagliato.
 */
.ns-row {
  box-sizing: border-box;
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) auto;
  column-gap: 12px;
  row-gap: 2px;
  align-items: baseline;
  width: 100%;
  min-height: 64px;
  padding: 12px 14px;
  border: 0;
  background: #ffffff;
  color: inherit;
  font: inherit;
  text-align: left;
  -webkit-tap-highlight-color: transparent;
}

button.ns-row:active {
  background: #f5f6f8;
}

.ns-row__date {
  grid-row: 1 / span 4;
  align-self: start;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 4px 0;
  border-radius: 8px;
  line-height: 1.1;
}

.ns-row--trip .ns-row__date {
  background: #fff1e6;
  color: #9a4200;
}

.ns-row--expense .ns-row__date {
  background: #e8f0fb;
  color: #1f4f8f;
}

.ns-row__date strong {
  font-size: 18px;
}

.ns-row__date small {
  font-size: 12px;
  font-weight: 700;
}

.ns-row__head {
  grid-column: 2;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ns-row__line {
  grid-column: 2 / 4;
}

.ns-row__clip {
  width: 17px;
  height: 17px;
  margin-left: 2px;
  vertical-align: -3px;
  color: var(--ns-muted);
}

.ns-row__meta,
.ns-row__note {
  font-size: 14px;
  color: var(--ns-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ns-row__note {
  font-style: italic;
}

.ns-row__amount {
  grid-column: 3;
  grid-row: 1;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.ns-row__tag {
  margin-left: 4px;
  padding: 1px 6px;
  border-radius: 6px;
  background: #fff3cd;
  color: #5c4400;
  font-size: 12px;
  font-weight: 700;
}

.ns-month__actions {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 3;
  display: flex;
  gap: 10px;
  max-width: 560px;
  margin: 0 auto;
  padding: 12px;
  padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
  background: rgba(242, 243, 245, 0.96);
  border-top: 1px solid #dfe2e6;
}

.ns-month__add {
  flex: 1;
  min-height: 56px;
  border-radius: 14px;
  font: inherit;
  font-size: 18px;
  font-weight: 800;
  -webkit-tap-highlight-color: transparent;
}

.ns-month__add--trip {
  border: 0;
  background: var(--ns-primary);
  background: color-mix(in srgb, var(--ns-primary) 88%, #000000);
  color: #ffffff;
}

.ns-month__add--expense {
  border: 2px solid var(--ns-primary);
  background: #ffffff;
  color: #1d2129;
}

.ns-month__add:active {
  transform: scale(0.98);
}
</style>
